import Cookie from '/app/utils/cookie';
import { api } from '/app/plugins/services';
import { createSharedComposable } from '@vueuse/core';
import { computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePlatform } from '/app/composables/platform';
import { Session, forceReauth } from '/app/utils/auth';
import { config } from '/app/config';
import { redirectTo } from '/app/utils/navigation';

const auth = reactive({
    current: { user: {} },
    authenticating: false,
    user: {},
    profile: {},
    isAdmin: false,
    isAmbassador: false,
    authenticated: false,
});

// ACTIVED is the API's spelling, typo and all. Fix it here when the API fixes
// it, or this check silently stops matching.
const SETTLED_RECRUITMENT = 'ACTIVED';

/**
 * Drop the scout cookie once the recruitment it credits has been settled.
 *
 * handleScout writes scout_id for 270 days, and applyIdentity replays it on
 * every scouted POST. Once the API reports the recruitment as activated the
 * attribution is already recorded server-side, so the cookie has nothing left
 * to earn and must go.
 */
const clearSettledScout = (account) => {
    const status = account?.recruitment?.status;

    if (status !== SETTLED_RECRUITMENT) {
        return;
    }

    if (!Cookie.has('scout_id')) {
        return;
    }

    // handleScout writes it domain-scoped; the host-only variant is cleared too
    // in case the account app or an older build left one behind.
    Cookie.delete('scout_id', true);
    Cookie.delete('scout_id', false);
};

export const useAuth = createSharedComposable(() => {
    let loadMePromise = null;

    const setAuthenticating = (value) => {
        auth.authenticating = value;
    }
    const setAccount = (account) => {
        // ensure handle is set
        if (!account.user.handle) {
            account.user.handle = account.user.screen_name;
        }

        auth.current = account;
        auth.user = account.user;
        auth.profile = account.profile || {};
        auth.isAdmin = account.admin_roles && account.admin_roles.global_admin;
        auth.isAmbassador = account.admin_roles && account.admin_roles.platform_ambassador;
        auth.authenticated = Session.exists();
    };

    const loadMe = async () => {
        // If already authenticated, return immediately
        if (auth.authenticated) {
            return Promise.resolve(auth.current);
        }

        // If a request is already in progress, return the existing promise
        if (loadMePromise) {
            return loadMePromise;
        }

        // Create a new promise for this request
        loadMePromise = (async () => {
            try {
                setAuthenticating(true);
                const { data: response } = await api.me.detailedMe();

                setAccount(response.data);
                clearSettledScout(response.data);
                setAuthenticating(false);
                
                return response.data;
            } catch (error) {
                setAuthenticating(false);

                // A session token exists but the API rejected it: the session
                // is stale, so force re-authentication. Network errors and
                // anonymous visitors (guards call loadMe without a session)
                // must not redirect.
                if (Session.exists() && error?.response?.status === 401) {
                    forceReauth();
                }
            } finally {
                // Reset the promise after completion so future calls can retry
                loadMePromise = null;
            }
        })();

        return loadMePromise;
    }

    const user = computed(() => auth.current.user);

    return {
        setAuthenticating,
        setAccount,
        loadMe,
        auth,
        user,
    };
});

export const useUser = createSharedComposable(() => {
    const { loadMe, user } = useAuth();

    return {
        refetch: loadMe,
        user,
    };
});

export const useLinks = createSharedComposable(() => {
    const { platform } = usePlatform();
    const accountURL = config.account.url;
    const route = useRoute();
    const router = useRouter();

    const isHome = computed(() => route.name === 'home.challenges');
    const scoutId = computed(() => {
        const id = Cookie.get('scout_id')

        if (id && id !== 'undefined' && id !== 'null') {
            return id;
        }
    });
    const homeURL = computed(() => {
        const to = router.resolve({ name: 'home.challenges' });
        return encodeURIComponent(`${location.origin}${to ? to.href : ''}`);
    });

    const continueURL = computed(() => {
        let fullPath = route.fullPath;

        if (isHome.value) {
            fullPath = '/me/my-dashboard'
        }

        const path = `${location.origin}${fullPath}`;

        return new URL(path)
    });

    const createLoginUrl = (params = {}) => {
        const url = new URL(`${accountURL}/gateway`);

        Object.keys(params).forEach(key => {
            url.searchParams.append(key, params[key]);
        });

        return url.toString();
    }

    const createConnectUrl = (params = {}) => {
        const url = new URL(`${accountURL}/connect`);

        if (!params.code) {
            throw new Error('Code is required');
        }

        Object.keys(params).forEach(key => {
            url.searchParams.append(key, params[key]);
        });

        return url;
    }
    const loginURL = computed(() => {
        const url = new URL(`${accountURL}/gateway`);

        url.searchParams.append('continue', continueURL.value);
        url.searchParams.append('platform', platform.value.id);

        if (scoutId.value) {
            url.searchParams.append('scout_id', scoutId.value);
        }

        return url.toString();
    });
    const signupURL = computed(() => {
        const url = new URL(`${accountURL}/signup`);

        url.searchParams.append('continue', encodeURIComponent(continueURL.value.toString()));
        url.searchParams.append('platform', platform.value.id);

        if (scoutId.value) {
            url.searchParams.append('scout_id', scoutId.value);
        }

        return url.toString();
    });

    const signout = () => {
        const query = new URLSearchParams();
        
        query.append('continue', homeURL.value);
        query.append('platform', platform.value.id);

        const url = new URL(`${accountURL}/logout`);
        url.search = query.toString();

        // Remove all cookies
        Session.remove();

        redirectTo(url.toString());
    }

    const signin = () => {
        redirectTo(loginURL.value);
    }

    return {
        isHome,
        scoutId,
        homeURL,
        continueURL,
        loginURL,
        signupURL,
        signout,
        signin,
        createLoginUrl,
        createConnectUrl,
    }
});